import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiHome, FiShield, FiPlay, FiUsers, FiMail, FiTv, FiAlertTriangle, FiBookOpen } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/', icon: FiHome },
    { name: 'Live TV', path: '/live-tv', icon: FiTv },
    { name: 'Types of Scams', path: '/types-of-scams', icon: FiAlertTriangle },
    { name: 'Blog', path: '/blog', icon: FiBookOpen },
    { name: 'Watch', path: 'http://link.lrcs.nyc/YTSL', icon: FiPlay, external: true },
    { name: 'Scam Help', path: '/scam-assistance', icon: FiShield },
    { name: 'Membership', path: '/#membership', icon: FiUsers },
    { name: 'Contact', path: '/#contact', icon: FiMail }
  ];

  const isActive = (path) => {
    if (path.startsWith('#')) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path) && path !== '/';
  };

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800 relative overflow-hidden">
      {/* NYC Background Image */}
      <div className="absolute inset-0 z-0">
        <img src="https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="New York City Skyline" className="w-full h-full object-cover opacity-20" />
        {/* Dark gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/90 to-black/95"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              {/* Updated logo using the S3 hosted image */}
              <img src="https://s3.us-east-1.wasabisys.com/lrcstv/CDN/LRCS-web-Logo" alt="Linda Roberts Calls Scammers" className="h-12 w-auto transition-transform duration-300 group-hover:scale-105" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              item.external ? (
                <a 
                  key={item.name} 
                  href={item.path} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 text-gray-300 hover:text-[#ffc700] font-medium transition-colors duration-300"
                >
                  <SafeIcon icon={item.icon} className="text-lg group-hover:scale-110 transition-transform" />
                  <span>{item.name}</span>
                </a>
              ) : (
                <Link 
                  key={item.name} 
                  to={item.path} 
                  onClick={() => handleNavClick(item.path)}
                  className={`group flex items-center gap-2 font-medium transition-colors duration-300 ${isActive(item.path) ? 'text-[#ffc700]' : 'text-gray-300 hover:text-[#ffc700]'}`}
                >
                  <SafeIcon icon={item.icon} className="text-lg group-hover:scale-110 transition-transform" />
                  <span>{item.name}</span>
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link 
              to="/scam-assistance" 
              className="bg-gradient-to-r from-[#ffc700] to-[#ffb700] hover:from-[#ffb700] hover:to-[#ffa700] text-black px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 flex items-center gap-2 shadow-lg"
            >
              <SafeIcon icon={FiShield} className="text-lg" />
              Get Help
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-gray-300 hover:text-[#ffc700] transition-colors p-2 relative z-20"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div 
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: isMenuOpen ? 1 : 0, height: isMenuOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3 }}
        className="lg:hidden overflow-hidden bg-black/98 border-t border-gray-800 relative z-10"
      >
        <div className="container mx-auto px-6 py-6">
          <nav className="space-y-4">
            {navigation.map((item) => (
              item.external ? (
                <a 
                  key={item.name} 
                  href={item.path} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 text-gray-300 hover:text-[#ffc700] font-medium py-3 border-b border-gray-800 transition-colors"
                >
                  <SafeIcon icon={item.icon} className="text-xl" />
                  <span>{item.name}</span>
                </a>
              ) : (
                <Link 
                  key={item.name} 
                  to={item.path} 
                  onClick={() => handleNavClick(item.path)}
                  className={`flex items-center gap-3 font-medium py-3 border-b border-gray-800 transition-colors ${isActive(item.path) ? 'text-[#ffc700]' : 'text-gray-300 hover:text-[#ffc700]'}`}
                >
                  <SafeIcon icon={item.icon} className="text-xl" />
                  <span>{item.name}</span>
                </Link>
              )
            ))}
          </nav>

          {/* Mobile CTA */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <Link 
              to="/scam-assistance" 
              onClick={() => setIsMenuOpen(false)}
              className="w-full bg-gradient-to-r from-[#ffc700] to-[#ffb700] hover:from-[#ffb700] hover:to-[#ffa700] text-black px-6 py-4 rounded-full font-bold text-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-lg"
            >
              <SafeIcon icon={FiShield} className="text-xl" />
              Get Scam Help
            </Link>
          </div>
        </div>
      </motion.div>
    </header>
  );
};

export default Header;